import { useMutation } from "@tanstack/react-query";
import QRCode from "qrcode";
import { Copy, Printer, QrCode, Trash2 } from "lucide-react";
import React from "react";
import { toast } from "sonner";

import { issueBadge, revokeBadge } from "@/api/admin";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";
import { apiErrorText } from "@/lib/scan-ui/screen";

/**
 * Пропуск с QR: выдача, печать, отзыв.
 *
 * Код показывается ОДИН раз — сразу после выдачи. В базе от него хэш, поэтому
 * «открыть карточку и посмотреть QR ещё раз» технически невозможно, и это не
 * недоделка: иначе секрет пришлось бы хранить открытым, и вся разница с
 * паролем на наклейке исчезла бы. Потерян пропуск — перевыпустить.
 */
export function BadgeCard({
  userId,
  username,
  issuedAt,
  onChanged,
}: {
  userId: number;
  username: string;
  issuedAt?: string | null;
  onChanged: () => void;
}): React.ReactElement {
  // Держим И картинку, И саму строку: печатают её не нашим PNG, а своим
  // кодировщиком, и без текста QR пришлось бы расшифровывать сканером.
  const [issued, setIssued] = React.useState<{ png: string; code: string } | null>(null);

  const issue = useMutation({
    mutationFn: () => issueBadge(userId),
    onSuccess: async (data) => {
      // Рисуем в data-URL, а не в canvas на месте: этот же PNG уходит в окно
      // печати, и второй способ отрисовки разъехался бы с первым.
      const png = await QRCode.toDataURL(data.code, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 512,
      });
      setIssued({ png, code: data.code });
      onChanged();
    },
    onError: (e) => toast.error(apiErrorText(e)),
  });

  const revoke = useMutation({
    mutationFn: () => revokeBadge(userId),
    onSuccess: () => {
      setIssued(null);
      toast.success("Пропуск погашен. Вход по паролю продолжает работать.");
      onChanged();
    },
    onError: (e) => toast.error(apiErrorText(e)),
  });

  /** Печать наклейки: отдельное окно с одним QR и логином под ним.
   *
   *  Строку кода в НАКЛЕЙКУ намеренно не печатаем, хотя на экране она есть:
   *  на пропуске она читалась бы глазами и переписывалась вручную, то есть
   *  секрет стало бы можно унести, не поднося сканер.
   *
   *  Логин печатается рядом намеренно — стопка одинаковых чёрных квадратов
   *  без подписи не раскладывается по людям. Секрета в подписи нет: логин и
   *  так знают все, а войти по нему без пропуска или пароля нельзя. */
  const print = () => {
    if (!issued) return;
    const w = window.open("", "_blank", "width=420,height=560");
    if (!w) {
      toast.error("Браузер заблокировал окно печати — разрешите всплывающие окна.");
      return;
    }
    w.document.write(
      `<!doctype html><html><head><title>Пропуск ${username}</title>` +
        `<style>body{margin:0;display:flex;flex-direction:column;align-items:center;` +
        `justify-content:center;height:100vh;font-family:system-ui,sans-serif}` +
        `img{width:60mm;height:60mm}p{margin:4mm 0 0;font-size:14pt;font-weight:600}` +
        `</style></head><body>` +
        `<img src="${issued.png}"><p>${username}</p>` +
        `<script>window.onload=()=>{window.print();}<\/script>` +
        `</body></html>`,
    );
    w.document.close();
  };

  return (
    <div className="rounded-lg border p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold">Пропуск с QR</span>
        {issuedAt && (
          <span className="text-xs text-muted-foreground">
            выдан {fmtDate(issuedAt)}
          </span>
        )}
      </div>

      {issued ? (
        <div className="flex flex-col items-center gap-2 rounded-md bg-amber-50 p-3">
          <img src={issued.png} alt="QR пропуска" className="size-48" />

          {/* Строка, закодированная в QR. Печатают пропуска своим макетом,
              кодируя QR заново, — без текста его пришлось бы считывать
              сканером с нашей картинки. Секрета это не добавляет: ровно то
              же самое лежит в самом коде, который наклеят на виду. */}
          <div className="w-full">
            <div className="mb-1 text-center text-[11px] text-amber-900">
              Закодировано в QR — скопируйте, если печатаете своим макетом:
            </div>
            <code className="block select-all break-all rounded border border-amber-300 bg-white px-2 py-1.5 text-center font-mono text-[13px]">
              {issued.code}
            </code>
          </div>

          <p className="text-center text-xs text-amber-900">
            Это единственный показ кода — в базе хранится только его отпечаток.
            Напечатайте и наклейте на пропуск. Потеряли — перевыпустите.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void navigator.clipboard
                  .writeText(issued.code)
                  .then(() => toast.success("Код скопирован"))
                  .catch(() =>
                    // Без https и разрешения буфера обмена нет — строка
                    // выделяема (`select-all`), скажем об этом прямо.
                    toast.error("Буфер обмена недоступен — выделите строку и скопируйте"),
                  );
              }}
            >
              <Copy className="mr-1 size-4" />
              Копировать
            </Button>
            <Button size="sm" variant="outline" onClick={print}>
              <Printer className="mr-1 size-4" />
              Печать
            </Button>
          </div>
        </div>
      ) : (
        <p className="mb-2 text-xs text-muted-foreground">
          {issuedAt
            ? "Пропуск выдан. Показать код заново нельзя — только перевыпустить."
            : "Пропуска нет: сотрудник входит логином и паролем."}
        </p>
      )}

      <div className="mt-2 flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="flex-1"
          disabled={issue.isPending}
          onClick={() => issue.mutate()}
        >
          <QrCode className="mr-1 size-4" />
          {issuedAt ? "Перевыпустить" : "Выдать пропуск"}
        </Button>
        {issuedAt && (
          <Button
            size="sm"
            variant="ghost"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate()}
            title="Погасить пропуск (пароль не трогаем)"
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
